import { YonduOAuthInteraction } from '@ver.id/libs.ui.yondu';
import {
  driveEmbeddedResume,
  MAX_RESUME_ITERATIONS,
} from './driveEmbeddedFlow';

// Timeout (30s) for each embedded resume advance.
const RESUME_TIMEOUT_MS = 30_000;

/**
 * Outcome of following an embedded authorization to its end.
 *
 * `error` carries the OAuth error code reported by the resume endpoint;
 * `maxIterationsExceeded` means the flow kept selecting new interactions past
 * the bounded cap, which points at a server-side loop.
 */
export type EmbeddedResumeLoopResult =
  | { status: 'completed' }
  | { status: 'error'; error: string }
  | { status: 'maxIterationsExceeded' };

/**
 * runEmbeddedResumeLoop — advances an already-finalized interaction through the
 * embedded resume endpoint until the authorization completes, errors, or the
 * iteration cap is hit.
 *
 * In embedded mode the resume endpoint must be fetched rather than navigated
 * to, and it may hand back a *new* interaction to settle before resuming again.
 * Each such interaction is started and finalized with consent here.
 *
 * @param interaction — the finalized interaction to resume from.
 * @param onInteractionAdvanced — called with every follow-up interaction so the
 *   caller can keep its own notion of "the current interaction" in sync.
 */
export async function runEmbeddedResumeLoop(
  interaction: YonduOAuthInteraction,
  onInteractionAdvanced: (next: YonduOAuthInteraction) => void,
): Promise<EmbeddedResumeLoopResult> {
  let resumeUri = interaction.getResumeUri();

  for (let i = 0; i < MAX_RESUME_ITERATIONS; i++) {
    const result = await driveEmbeddedResume(
      resumeUri,
      AbortSignal.timeout(RESUME_TIMEOUT_MS),
    );

    if (result.status === 'completed') return { status: 'completed' };
    if (result.status === 'error') {
      return { status: 'error', error: result.error };
    }

    // interactionSelected: settle the next interaction, then resume again.
    const next = YonduOAuthInteraction.fromURIs({
      authorization: result.authorization,
      interaction: result.interaction,
      startUri: result.startUri,
      finalizeUri: result.finalizeUri,
      resumeUri: result.resumeUri,
    });
    onInteractionAdvanced(next);
    await next.start();
    await next.finalize({ consentGiven: true });
    resumeUri = next.getResumeUri();
  }

  return { status: 'maxIterationsExceeded' };
}
